import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineS } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { cart } from '@sorare/core/src/lib/glossary';

import { useCart } from 'hooks/useCart';

import { ClearButton } from '../ClearButton';
import { SavedForLaterItem } from '../SavedForLaterItem';
import type { SavedForLaterItems_cartItem } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 2 })`
  border: 2px solid var(--c-nd-150);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
`;
const Header = styled(SBHorizontal).attrs({ gap: 0.5 })`
  align-items: baseline;
`;

const Wrapper = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const SavedForLaterItemSkeleton = styled(Skeleton)`
  max-width: 112px;
  min-width: 92px;
  width: 100%;
  min-height: 336px;

  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-50);
`;

type Props = {
  cartItems: SavedForLaterItems_cartItem[];
  onStatsClick: (playerSlug: string) => void;
};

export const SavedForLaterItems = ({ cartItems, onStatsClick }: Props) => {
  const { savedForLaterCount, removeFromCart } = useCart();
  const { formatMessage } = useIntlContext();
  if (!savedForLaterCount) return null;
  return (
    <Root>
      <Header>
        <HeadlineS as="h2">
          <FormattedMessage {...cart.savedForLater} />({savedForLaterCount})
        </HeadlineS>
        <ClearButton
          onClear={async () => {
            await removeFromCart.mutate(
              cartItems.map(cartItem => cartItem.id),
              cartItems?.[0].card.sport
            );
          }}
          title={
            <FormattedMessage
              id="savedForLaterItems.clearSavedForLater.title"
              defaultMessage="Clear saved for later?"
            />
          }
          message={
            <FormattedMessage
              id="savedForLaterItems.clearSavedForLater.message"
              defaultMessage="Are you sure you want to delete {value, plural, =1 {this card} other {these # cards}} from Saved for later? This cannot be undone"
              values={{ value: cartItems.length }}
            />
          }
          cta={formatMessage(cart.clearAll)}
        >
          <FormattedMessage {...cart.clearAll} />
        </ClearButton>
      </Header>
      <Wrapper>
        {cartItems.map(cartItem => (
          <SavedForLaterItem
            key={cartItem.id}
            cartItem={cartItem}
            card={cartItem.card}
            onStatsClick={() => onStatsClick(cartItem.card.anyPlayer.slug)}
          />
        ))}
        {savedForLaterCount - cartItems?.length > 0 &&
          Array.from(
            { length: savedForLaterCount - cartItems?.length || 0 },
            (_, i) => i
          ).map(i => <SavedForLaterItemSkeleton key={i} />)}
      </Wrapper>
    </Root>
  );
};

SavedForLaterItems.fragments = {
  cartItem: gql`
    fragment SavedForLaterItems_cartItem on CartItem {
      id
      card {
        slug
        sport
        anyPositions
        anyPlayer {
          slug
        }
        ...SavedForLaterItem_anyCard
      }
      ...SavedForLaterItem_cartItem
    }
    ${SavedForLaterItem.fragments.anyCard}
    ${SavedForLaterItem.fragments.cartItem}
  ` as TypedDocumentNode<SavedForLaterItems_cartItem>,
};
